import { ConfigDefaults, ConfigOpretRetteDefaults } from "../constants/config";
import {
  PagedPersonsQueryField,
  PersonAbonnementerField,
  PersonArtField,
  PersonBeskikkelserField,
  PersonBetalingsAdrField,
  PersonBidragsfakturaField,
  PersonDisciplinaerStatusField,
  PersonEfteruddannelseField,
  PersonFirmaRelationField,
  PersonForeningField,
  PersonForsikringField,
  PersonGennemfoerteTilsynField,
  PersonHistorik2Field,
  PersonHistorikField,
  PersonIdKortField,
  PersonInteresseField,
  PersonKlienterklaeringField,
  PersonKontaktadresseField,
  PersonKorrespondanceField,
  PersonKursistField,
  PersonPrincipalPersonFuldmField,
  PersonPrincipalPersonPrincipalNavigationField,
  PersonSanktionField,
  PersonTilsynerklaeringField,
  PersonTyperField,
  PersonUdvalgField,
  RetssagsProeveField,
  UdenlandskAdvokatsamfundField,
} from "../interfaces/personer";
import { Fmuretssagsproeve } from "../interfaces/schema";
import { IColumn } from "../interfaces/table";
import { formatLessons } from "../util/number";
import { Field } from "../util/typeUtil";

export const defaultPersonColumns: IColumn<PagedPersonsQueryField>[] = [
  {
    field: "personart",
    title: "Personart",
    active: true,
    format: "custom",
    customValue: (rowData) => {
      if (typeof rowData === "object" && rowData !== null) {
        if ("personart" in rowData) {
          const replaced = (rowData.personart as string)
            .replace("OE", "Ø")
            .replace("AE", "Æ");
          const titleCase =
            replaced.charAt(0).toLocaleUpperCase("da-DK") +
            replaced.slice(1).toLocaleLowerCase("da-DK");
          return titleCase;
        }
      }
      return "";
    },
  },
  {
    field: "erUdoevende",
    title: "Status",
    active: true,
    format: "custom",
    customValue: (rowData) => {
      if (typeof rowData === "object" && rowData !== null) {
        if ("erUdoevende" in rowData) {
          return rowData.erUdoevende ? "Udøvende" : "Ikke udøvende";
        }
      }
      return "";
    },
  },
  {
    field: "kundeNr",
    title: "Kundenummer",
    active: true,
    format: "number",
  },
  {
    field: "fuldtNavn",
    title: "Navn",
    active: true,
    format: "string",
  },
  {
    field: "primaerPersonFirmaRelation.firma.navn1",
    title: "Firmanavn",
    active: true,
    format: "string",
  },
  {
    field: "adresse.adresse.adresselinie1",
    title: "Adresselinje 1",
    active: true,
    format: "string",
  },
  {
    field: "adresse.adresse.adresselinie2",
    title: "Adresselinje 2",
    active: true,
    format: "string",
  },
  {
    field: "adresse.adresse.postnummer",
    title: "Postnummer",
    active: true,
    format: "number",
  },
  {
    field: "adresse.adresse.bynavn",
    title: "By",
    active: true,
    format: "string",
  },
  {
    field: "primaerPersonFirmaRelation.emailArbejde",
    title: "Email (Arbejde)",
    active: true,
    format: "string",
  },
  {
    field: "emailPrivat",
    title: "Email (Privat)",
    active: true,
    format: "string",
  },
  {
    field: "prioriteretEmail",
    title: "Email (Prioriteret)",
    active: true,
    format: "string",
  },
  {
    field: "fornavne",
    title: "Fornavn(e)",
    active: false,
    format: "string",
  },
  {
    field: "efternavn",
    title: "Efternavn",
    active: false,
    format: "string",
  },
  {
    field: "primaerPersonFirmaRelation.firma.emailSikker",
    title: "Email (Sikker)",
    active: false,
    format: "string",
  },
  {
    field: "primaerPersonFirmaRelation.firma.firmaNr",
    title: "Firmanummer",
    active: false,
    format: "number",
  },
  {
    field: "adresse.adresse.landNavigation.navn",
    title: "Land",
    active: false,
    format: "string",
  },
  {
    field: "tlfMobilNr",
    title: "Telefonnummer",
    active: false,
    format: "number",
  },
  {
    field:
      "primaerPersonFirmaRelation.firma.retskredsNrNavigation.advokatkredsNrNavigation.navn",
    title: "Advokatkreds",
    active: false,
    format: "string",
  },
  {
    field: "koen",
    title: "Køn",
    active: false,
    format: "custom",
    customValue: (rowData) => {
      switch ((rowData as any).koen) {
        case "M":
          return "Mand";
        case "K":
          return "Kvinde";
        case "N":
          return "Nonbinær";
        default:
          return "";
      }
    },
  },
  {
    field: "alder",
    title: "Alder",
    active: false,
    format: "number",
  },
  {
    field: "foedselsDato",
    title: "Fødselsdato",
    active: false,
    format: "date",
  },
  {
    field: "foersteBeskikkelse.periode.fraDato",
    title: "Første beskikkelsesdato",
    active: false,
    format: "date",
  },
  {
    field: "senesteGenudlevering.periode.fraDato",
    title: "Sidste genudlevering",
    active: false,
    format: "date",
  },
  {
    field: "senesteDeponering.periode.fraDato",
    title: "Sidste deponering",
    active: false,
    format: "date",
  },
  {
    field: "debitorInfo.fakturaAfsendelse",
    title: "Afs.std.",
    active: false,
    format: "string",
  },
  {
    field: "debitorInfo.email",
    title: "E-mail (faktura)",
    active: false,
    format: "string",
  },
  {
    field: "moederetLandsret",
    title: "Møderet L",
    active: false,
    format: "boolean",
  },
  {
    field: "moederetHoejesteret",
    title: "Møderet H",
    active: false,
    format: "boolean",
  },
  {
    field: "titel.navn",
    title: "Titel 1",
    active: false,
    format: "string",
  },
  {
    field: "titel1.navn",
    title: "Titel 2",
    active: false,
    format: "string",
  },
  {
    field: "primaerPrincipal.fuldtNavn",
    title: "Nuværende Principals Navn",
    active: false,
    format: "string",
  },
  {
    field: "debitorInfo.ean",
    title: "EAN",
    active: false,
    format: "number",
  },
  {
    field: "id",
    title: "Id",
    active: false,
    format: "string",
  },
  {
    field: "opretDato",
    title: "Oprettet",
    active: false,
    format: "date",
  },
  {
    field: "opretInit",
    title: "Oprettet af",
    active: false,
    format: "string",
  },
  {
    field: "retteDato",
    title: "Sidst rettet",
    active: false,
    format: "date",
  },
  {
    field: "retteInit",
    title: "Rettet af",
    active: false,
    format: "string",
  },
];

export const defaultPersonHistorikColumns: IColumn<PersonHistorikField>[] = [
  { field: "id", title: "Id", active: true, format: "string" },
  { field: "titel1", title: "Titel 1", active: true, format: "string" },
  { field: "titel2", title: "Titel 2", active: false, format: "string" },
  { field: "efternavn", title: "Efternavn", active: true, format: "string" },
  { field: "fornavne", title: "Fornavne", active: true, format: "string" },
  { field: "adr1", title: "Adr 1", active: false, format: "string" },
  { field: "husNr", title: "Hus nr.", active: false, format: "string" },
  { field: "husNrBogstav", title: "Bokstav", active: false, format: "string" },
  { field: "etageNr", title: "Etage", active: false, format: "number" },
  { field: "side", title: "Side", active: false, format: "string" },
  { field: "adr2", title: "Adr 2", active: false, format: "string" },
  { field: "adr3", title: "Adr 3", active: false, format: "string" },
  { field: "adr4", title: "Adr 4", active: false, format: "string" },
  { field: "postNr", title: "Postnr.", active: false, format: "string" },
  { field: "landeKode", title: "Landekode", active: false, format: "string" },
  { field: "tlfPrivat", title: "Tlf. privat", active: false, format: "string" },
  { field: "tlfMobilNr", title: "Tlf. mobil", active: false, format: "string" },
  { field: "tlfFaxNr", title: "Fax nr.", active: false, format: "string" },
  {
    field: "emailPrivat",
    title: "E-mail privat",
    active: false,
    format: "string",
  },
  {
    field: "webAdresse",
    title: "Web adresse",
    active: false,
    format: "string",
  },
  { field: "personart", title: "Personart", active: false, format: "string" },
  {
    field: "foedselsDato",
    title: "Fødselsdato",
    active: true,
    format: "date",
  },
  { field: "mkmark", title: "Køn", active: false, format: "string" },
  //{ field: "ukendt", title: "Ukendt adr", active: false, format: "string" },
  { field: "regionNavn", title: "Region", active: false, format: "string" },
  { field: "kommuneNavn", title: "Kommune", active: false, format: "string" },
  { field: "kredsNavn", title: "Kreds", active: false, format: "string" },
  { field: "tlfArbejde", title: "Tlf arb.", active: false, format: "string" },
  {
    field: "emailArbejde",
    title: "E-mail arbejde",
    active: false,
    format: "string",
  },
  {
    field: "afsendelsesformStd",
    title: "Std. afsendelsesform",
    active: false,
    format: "string",
  },
  {
    field: "personDoedDato",
    title: "Person død dato",
    active: false,
    format: "date",
  },
  {
    field: "personFirmaNavn",
    title: "Person firma navn",
    active: false,
    format: "string",
  },
  {
    field: "advokatsamfundudlNavn",
    title: "Udenlandsk advokatsamfund navn",
    active: false,
    format: "string",
  },
  {
    field: "emailFirma",
    title: "E-mail firma",
    active: false,
    format: "string",
  },
  {
    field: "emailSikkerFirma",
    title: "E-mail sikker firma",
    active: false,
    format: "string",
  },
  { field: "retskreds", title: "Retskreds", active: false, format: "string" },
  { field: "land", title: "Land", active: false, format: "string" },
  { field: "personId", title: "PersonId", active: false, format: "string" },
  { field: "opretDato", title: "Oprettet", active: true, format: "date" },
  { field: "opretInit", title: "Oprettet af", active: true, format: "string" },
];

export const defaultPersonHistorik2Columns: IColumn<PersonHistorik2Field>[] = [
  {
    field: "snapshotTid",
    title: "Snapshot Tid",
    active: true,
    format: "date",
  },
  { field: "personart", title: "Personart", active: true, format: "string" },
  { field: "erUdoevende", title: "Udøvende", active: true, format: "boolean" },
  { field: "personId", title: "Person ID", active: true, format: "string" },
  { field: "kundeNr", title: "Kundenr.", active: true, format: "number" },
  { field: "titel1", title: "Titel 1", active: true, format: "string" },
  { field: "titel2", title: "Titel 2", active: true, format: "string" },
  { field: "fornavne", title: "Fornavn(e)", active: true, format: "string" },
  { field: "efternavn", title: "Efternavn", active: true, format: "string" },
  { field: "foedselsDato", title: "Fødselsdato", active: true, format: "date" },
  {
    field: "tlfMobilNr",
    title: "Telefon (mobil)",
    active: true,
    format: "number",
  },
  {
    field: "emailPrivat",
    title: "Email (privat)",
    active: true,
    format: "number",
  },
  {
    field: "fakturaAfsendelse",
    title: "Afsend standard",
    active: true,
    format: "string",
  },
  {
    field: "primaerFirmaFirmaNr",
    title: "Firmanr. (primær)",
    active: true,
    format: "string",
  },
  {
    field: "primaerFirmaNavn",
    title: "Firmanavn (primær)",
    active: true,
    format: "string",
  },
  {
    field: "primaerFirmaEmailArbejde",
    title: "Firma email (primær)",
    active: true,
    format: "string",
  },
  {
    field: "primaerFirmaEmailSikker",
    title: "Firma email sikker (primær)",
    active: true,
    format: "string",
  },
  {
    field: "primaerFirmaAdvokatkreds",
    title: "Kreds",
    active: true,
    format: "string",
  },
  {
    field: "adresseAdresselinie1",
    title: "Adresselinie 1",
    active: true,
    format: "string",
  },
  {
    field: "adresseAdresselinie2",
    title: "Adresselinie 2",
    active: true,
    format: "string",
  },
  {
    field: "adresseAdresselinie4",
    title: "Adresselinie 4",
    active: true,
    format: "string",
  },
  {
    field: "adressePostnummer",
    title: "Postnr.",
    active: true,
    format: "string",
  },
  { field: "adresseBynavn", title: "By", active: true, format: "string" },
  {
    field: "adresseLandekode",
    title: "Landekode",
    active: true,
    format: "string",
  },
  { field: "adresseCoNavn", title: "Co. navn", active: true, format: "string" },
  {
    field: "personDoedDato",
    title: "Dødsdato",
    active: true,
    format: "string",
  },
];

export const defaultPersonArtColumns: IColumn<PersonArtField>[] = [
  {
    field: "personartNrNavigation.navn",
    title: "Personart",
    active: true,
    format: "string",
  },
  {
    field: "periode.fraDato",
    title: "Startdato",
    active: true,
    format: "date",
  },
  {
    field: "periode.tilDatoInklusiv",
    title: "Slutdato",
    active: true,
    format: "date",
  },
  {
    field: "person.retteDato",
    title: "Sidst rettet",
    active: false,
    format: "date",
  },
  {
    field: "person.opretInit",
    title: "Oprettet af",
    active: false,
    format: "string",
  },
  {
    field: "person.retteInit",
    title: "Rettet af",
    active: false,
    format: "string",
  },
];

export const defaultPersonFirmaRelationColumns: IColumn<PersonFirmaRelationField>[] =
  [
    {
      field: "firma.navn1",
      title: "Firmanavn",
      active: true,
      format: "string",
      link: (personFirma: any) => `/firma/${personFirma.firma.id}`,
    },
    {
      field: "personfirmatypeNrNavigation.navn",
      title: "Relation",
      active: true,
      format: "string",
    },
    {
      field: "periode.fraDato",
      title: "Startdato",
      active: true,
      format: "date",
    },
    {
      field: "periode.tilDatoInklusiv",
      title: "Slutdato",
      active: true,
      format: "date",
    },
    {
      field: "primaereFirma",
      title: "Primære firma",
      active: true,
      format: "boolean",
    },
    {
      field: "priorieteretPrimaerFra",
      title: "Prioriteringsdato",
      active: true,
      format: "date",
    },
    {
      field: "emailArbejde",
      title: "E-mail arbejde",
      active: true,
      format: "string",
    },
    {
      field: "bemaerkninger",
      title: "Bemærkninger",
      active: true,
      format: "string",
    },
    {
      field: "fakturaReference",
      title: "Fakturareference advokatuddannelsen",
      active: true,
      format: "string",
    },
    {
      field: "firma.firmaNr",
      title: "Firmanr",
      active: false,
      format: "number",
    },
    ...ConfigDefaults,
  ];

export const defaultPersonBeskikkelserColumns: IColumn<PersonBeskikkelserField>[] =
  [
    {
      field: "beskikkelsestypeNavigation.navn",
      title: "Beskikkelsestype",
      active: true,
      format: "string",
    },
    {
      field: "periode.fraDato",
      title: "Startdato",
      active: true,
      format: "date",
    },
    {
      field: "periode.tilDatoInklusiv",
      title: "Slutdato",
      active: true,
      format: "date",
    },
    { field: "aarsag", title: "Årsag", active: true, format: "string" },
    { field: "id", title: "Id", active: false, format: "string" },
    { field: "opretDato", title: "Oprettet", active: false, format: "date" },
    {
      field: "retteDato",
      title: "Sidst rettet",
      active: false,
      format: "date",
    },
    {
      field: "opretInit",
      title: "Oprettet af",
      active: false,
      format: "string",
    },
    {
      field: "retteInit",
      title: "Rettet af",
      active: false,
      format: "string",
    },
  ];

export const defaultPersonTyperColumns: IColumn<PersonTyperField>[] = [
  {
    field: "persontypeNrNavigation.navn",
    title: "Rolle/kvalifikation",
    active: true,
    format: "string",
  },
  {
    field: "periode.fraDato",
    title: "Startdato",
    active: true,
    format: "date",
  },
  {
    field: "periode.tilDatoInklusiv",
    title: "Slutdato",
    active: true,
    format: "date",
  },

  { field: "id", title: "Id", active: false, format: "string" },
  { field: "opretInit", title: "Oprettet af", active: false, format: "string" },
  { field: "retteInit", title: "Rettet af", active: false, format: "string" },
  { field: "opretDato", title: "Oprettet", active: false, format: "date" },
  { field: "retteDato", title: "Sidst rettet", active: false, format: "date" },
  {
    field: "persontypeNr",
    title: "Persontype nr",
    active: false,
    format: "number",
  },
];

export const defaultPersonInteresseColumns: IColumn<PersonInteresseField>[] = [
  {
    field: "interesse.navn",
    title: "Interesse",
    active: true,
    format: "string",
  },
  {
    field: "interesse.forkortelse",
    title: "Forkortelse",
    active: true,
    format: "string",
  },
  { field: "id", title: "Id", active: false, format: "string" },
  { field: "opretDato", title: "Oprettet", active: false, format: "date" },
  { field: "retteDato", title: "Sidst rettet", active: false, format: "date" },
  { field: "opretInit", title: "Oprettet af", active: false, format: "string" },
  { field: "retteInit", title: "Rettet af", active: false, format: "string" },
];

export const defaultPersonPrincipalPersonPrincipalNavigationColumns: IColumn<PersonPrincipalPersonPrincipalNavigationField>[] =
  [
    {
      field: "personFuldm.fuldtNavn",
      title: "Principal for",
      active: true,
      format: "string",
      link: (rowdata) => `/person/${rowdata.personFuldm.id}`,
    },
    {
      field: "personFuldm.kundeNr",
      title: "Kundenr.",
      active: true,
      format: "number",
    },
    {
      field: "periode.fraDato",
      title: "Startdato",
      active: true,
      format: "date",
    },
    {
      field: "periode.tilDatoInklusiv",
      title: "Slutdato",
      active: true,
      format: "date",
    },
    { field: "id", title: "Id", active: false, format: "string" },
    { field: "opretDato", title: "Oprettet", active: false, format: "date" },
    {
      field: "retteDato",
      title: "Sidst rettet",
      active: false,
      format: "date",
    },
    {
      field: "opretInit",
      title: "Oprettet af",
      active: false,
      format: "string",
    },
    {
      field: "retteInit",
      title: "Rettet af",
      active: false,
      format: "string",
    },
  ];

export const defaultPersonPrincipalPersonFuldmColumns: IColumn<PersonPrincipalPersonFuldmField>[] =
  [
    {
      field: "personPrincipalNavigation.fuldtNavn",
      title: "Fuldmægtig under",
      active: true,
      format: "string",
      link: (rowdata) => `/person/${rowdata.personPrincipalNavigation.id}`,
    },
    {
      field: "personPrincipalNavigation.kundeNr",
      title: "Kundenr.",
      active: true,
      format: "number",
    },
    {
      field: "personPrincipalNavigation.prioriteretEmail",
      title: "Email",
      active: true,
      format: "string",
    },
    {
      field: "periode.fraDato",
      title: "Startdato",
      active: true,
      format: "date",
    },
    {
      field: "periode.tilDatoInklusiv",
      title: "Slutdato",
      active: true,
      format: "date",
    },
    { field: "id", title: "Id", active: false, format: "string" },
    { field: "opretDato", title: "Oprettet", active: false, format: "date" },
    {
      field: "retteDato",
      title: "Sidst rettet",
      active: false,
      format: "date",
    },
    {
      field: "opretInit",
      title: "Oprettet af",
      active: false,
      format: "string",
    },
    {
      field: "retteInit",
      title: "Rettet af",
      active: false,
      format: "string",
    },
  ];

export const defaultUdenlandskAdvokatsamfundColumns: IColumn<UdenlandskAdvokatsamfundField>[] =
  [
    {
      field: "udenlandskadvokatsamfundNavigation.navn",
      title: "Udenlandsk advokatsamfund",
      active: true,
      format: "string",
    },
    {
      field: "periode.fraDato",
      title: "Startdato",
      active: true,
      format: "date",
    },
    {
      field: "periode.tilDatoInklusiv",
      title: "Slutdato",
      active: true,
      format: "date",
    },
    { field: "id", title: "Id", active: false, format: "string" },
    {
      field: "bemaerkninger",
      title: "Land",
      active: false,
      format: "string",
    },
  ];

export const defaultPersonKursistColumns: IColumn<PersonKursistField>[] = [
  {
    field: "hold.navn",
    title: "Kursusnavn",
    active: true,
    format: "string",
    link: (rowdata) => `/kursus/${rowdata.hold.id}`,
  },
  {
    field: "deltagerStatusNavigation.navn",
    title: "Status",
    active: true,
    format: "string",
  },
  {
    field: "tilmeldDato",
    title: "Tilmeldt",
    active: true,
    format: "date",
  },
  { field: "hold.fraDato", title: "Startdato", active: true, format: "date" },
  { field: "hold.tilDato", title: "Slutdato", active: true, format: "date" },
  {
    field: "fakturaStatusNavigation.navn",
    title: "Faktureringsstatus",
    active: true,
    format: "number",
  },
  {
    field: "overnatningstypeNavigation.navn",
    title: "Overnatning",
    active: true,
    format: "string",
  },
  {
    field: "hold.hotelNavigation.navn1",
    title: "Hotel",
    active: true,
    format: "string",
  },
  {
    field: "hold.kursustypeNavigation.navn",
    title: "Kursustype",
    active: true,
    format: "string",
  },
  {
    field: "tilmeldtRaekkefoelge",
    title: "Eksamensnr.",
    active: true,
    format: "number",
  },
  { field: "id", title: "Id", active: false, format: "string" },
  { field: "opretDato", title: "Oprettet", active: false, format: "date" },
  { field: "retteDato", title: "Sidst rettet", active: false, format: "date" },
  { field: "opretInit", title: "Oprettet af", active: false, format: "string" },
  { field: "retteInit", title: "Rettet af", active: false, format: "string" },
  {
    field: "bestaaetMark",
    title: "Har bestået (eksamenskursus)",
    active: true,
    format: "custom",
    customValue: (value) => {
      if (value && typeof value === "string") {
        const titleCase =
          value.charAt(0).toLocaleUpperCase("da-DK") +
          value.slice(1).toLocaleLowerCase("da-DK");
        return titleCase;
      } else {
        return "";
      }
    },
  },
];

export const defaultRetssagsProeveColumns: IColumn<
  Field<Fmuretssagsproeve, 3>
>[] = [
  { field: "nr", title: "RP-nr.", active: true, format: "number" },
  {
    field: "proceduredato",
    title: "Dato",
    active: true,
    format: "date",
  },
  {
    field: "retNavigation.navn1",
    title: "Retskreds",
    active: true,
    format: "string",
  },
  {
    field: "censorNavigation.fuldtNavn",
    title: "Censor",
    active: true,
    format: "string",
    link: (retssagsproeve: Fmuretssagsproeve) =>
      `/person/${retssagsproeve.censorPersonId}`,
  },
  {
    field: "statusNavigation.navn",
    title: "Resultat",
    active: true,
    format: "string",
  },
  {
    field: "fakturaStatusNavigation.navn",
    title: "Faktureringsstatus",
    active: false,
    format: "number",
  },
  { field: "notat", title: "Notat", active: false, format: "string" },
  { field: "fiktiv", active: false, format: "boolean", title: "Fiktiv" },
  { field: "tidspunkt", active: false, format: "string", title: "Tidspunkt" },
  { field: "adresse", active: false, format: "string", title: "Adresse" },
  { field: "bsNummer", active: false, format: "string", title: "Bs-nummer" },
  {
    field: "dommernavn",
    active: false,
    format: "string",
    title: "Dommernavn",
  },
  {
    field: "antalDommere",
    active: false,
    format: "number",
    title: "Antal dommere",
  },
  ...ConfigOpretRetteDefaults,
];

export const defaultPersonKorrespondanceColumns: IColumn<PersonKorrespondanceField>[] =
  [
    { field: "id", title: "Id", active: false, format: "string" },
    { field: "opretDato", title: "Oprettet", active: true, format: "date" },
    {
      field: "beskrivelse",
      title: "Beskrivelse",
      active: true,
      format: "string",
    },
    { field: "navn", title: "Beskrivelse2", active: true, format: "string" },
    { field: "filnavn", title: "Filtype", active: true, format: "string" },
    {
      field: "opretInit",
      title: "Oprettet af",
      active: true,
      format: "string",
    },
    {
      field: "retteDato",
      title: "Sidst rettet",
      active: false,
      format: "date",
    },

    {
      field: "retteInit",
      title: "Rettet af",
      active: false,
      format: "string",
    },
    {
      field: "dokumentNr",
      title: "Dokument nr",
      active: false,
      format: "number",
    },
  ];

/**
 * ID-kort-bestillingernes fire felter. Status vises som badge og resten som datoer/tekst — det
 * er hele det, GraphQL henter for personIdKort, så der er ikke flere kolonner at slå til.
 * "Sendt" er sekretariatets ord for domænets Behandlet-status (ADV-234).
 */
export const defaultPersonIdKortColumns: IColumn<PersonIdKortField>[] = [
  { field: "status", title: "Status", active: true, format: "string" },
  { field: "opretDato", title: "Bestilt", active: true, format: "date" },
  {
    field: "ekspederingstidspunkt",
    title: "Sendt",
    active: true,
    format: "date",
  },
  { field: "ekspederetAf", title: "Sendt af", active: true, format: "string" },
];

export const defaultForeningPersonColumns: IColumn<PersonForeningField>[] = [
  {
    field: "forening.navn",
    title: "Forening",
    active: true,
    format: "string",
  },
  {
    field: "forening.forkortelse",
    title: "Forkortelse",
    active: true,
    format: "string",
  },
  {
    field: "periode.fraDato",
    title: "Startdato",
    active: true,
    format: "date",
  },
  {
    field: "periode.tilDatoInklusiv",
    title: "Slutdato",
    active: true,
    format: "date",
  },
  {
    field: "sidsteOpkraevning",
    title: "Sidste opkrævningsår",
    active: true,
    format: "number",
  },
  { field: "id", title: "Id", active: false, format: "string" },
  { field: "opretDato", title: "Oprettet", active: false, format: "date" },
  { field: "retteDato", title: "Sidst rettet", active: false, format: "date" },
  { field: "opretInit", title: "Oprettet af", active: false, format: "string" },
  { field: "retteInit", title: "Rettet af", active: false, format: "string" },
];

export const defaultPersonAbonnementerColumns: IColumn<PersonAbonnementerField>[] =
  [
    {
      field: "abonnementNrNavigation.navn",
      title: "Abonnement",
      active: true,
      format: "string",
    },
    {
      field: "periode.fraDato",
      title: "Startdato",
      active: true,
      format: "date",
    },
    {
      field: "periode.tilDatoInklusiv",
      title: "Slutdato",
      active: true,
      format: "date",
    },
    {
      field: "faktureres",
      title: "Faktureres",
      active: true,
      format: "boolean",
    },
    {
      field: "antalEksemplar",
      title: "Antal",
      active: true,
      format: "number",
    },
    { field: "id", title: "Id", active: false, format: "string" },
    { field: "opretDato", title: "Oprettet", active: false, format: "date" },
    {
      field: "retteDato",
      title: "Sidst rettet",
      active: false,
      format: "date",
    },
    {
      field: "opretInit",
      title: "Oprettet af",
      active: false,
      format: "string",
    },
    {
      field: "retteInit",
      title: "Rettet af",
      active: false,
      format: "string",
    },
    {
      field: "sidsteOpkraevning",
      title: "Sidste opkrævningsår",
      active: false,
      format: "number",
    },
  ];

export const defaultPersonUdvalgColumns: IColumn<PersonUdvalgField>[] = [
  {
    field: "udvalg.navn",
    title: "Udvalg",
    active: true,
    format: "string",
  },
  {
    field: "tillidshvervNrNavigation.navn",
    title: "Tillidshverv",
    active: true,
    format: "string",
  },
  {
    field: "periode.fraDato",
    title: "Startdato",
    active: true,
    format: "date",
  },
  {
    field: "periode.tilDatoInklusiv",
    title: "Slutdato",
    active: true,
    format: "date",
  },

  {
    field: "fratraadtAarsag",
    title: "Bemærkning",
    active: true,
    format: "string",
  },
  { field: "id", title: "Id", active: false, format: "string" },
  { field: "opretDato", title: "Oprettet", active: false, format: "date" },
  { field: "retteDato", title: "Sidst rettet", active: false, format: "date" },
  { field: "opretInit", title: "Oprettet af", active: false, format: "string" },
  { field: "retteInit", title: "Rettet af", active: false, format: "string" },
];

export const defaultKontaktadresseColumns: IColumn<PersonKontaktadresseField>[] =
  [
    {
      field: "adresse.adresselinie1",
      title: "Adresselinje 1",
      active: true,
      format: "string",
    },
    {
      field: "adresse.adresselinie2",
      active: false,
      format: "string",
      title: "Adresselinje 2",
    },
    {
      field: "adresse.postnummer",
      active: true,
      format: "string",
      title: "Postnummer",
    },
    {
      field: "adresse.bynavn",
      active: true,
      format: "string",
      title: "Bynavn",
    },
    {
      field: "adresse.adresselinie4",
      active: false,
      format: "string",
      title: "Adresselinje 4",
    },
    {
      field: "adresse.landNavigation.navn",
      active: true,
      format: "string",
      title: "Land",
    },
    {
      field: "periode.fraDato",
      active: true,
      format: "date",
      title: "Startdato",
    },
    {
      field: "periode.tilDatoInklusiv",
      active: true,
      format: "date",
      title: "Slutdato",
    },
    {
      field: "adresse.coNavn",
      active: false,
      format: "string",
      title: "Co",
    },
    {
      field: "bemaerkning",
      active: false,
      format: "string",
      title: "Bemærkning",
    },
  ];

export const defaultPersonBetalingsAdrColumns: IColumn<PersonBetalingsAdrField>[] =
  [
    {
      field: "adresse.adresselinie1",
      active: true,
      format: "string",
      title: "Adresselinje 1",
    },
    {
      field: "adresse.adresselinie2",
      active: true,
      format: "string",
      title: "Adresselinje 2",
    },
    {
      field: "adresse.postnummer",
      active: true,
      format: "string",
      title: "Postnummer",
    },
    {
      field: "adresse.bynavn",
      active: true,
      format: "string",
      title: "Bynavn",
    },
    {
      field: "adresse.adresselinie4",
      active: true,
      format: "string",
      title: "Adresselinje 4",
    },
    {
      field: "adresse.landNavigation.navn",
      active: true,
      format: "string",
      title: "Land",
    },
    {
      field: "periode.fraDato",
      title: "Startdato",
      active: false,
      format: "date",
    },
    {
      field: "periode.tilDatoInklusiv",
      title: "Slutdato",
      active: false,
      format: "date",
    },
    {
      field: "email",
      title: "Email",
      active: true,
      format: "string",
    },
    {
      field: "bemaerkning",
      title: "Bemærkninger",
      active: true,
      format: "string",
    },
    ...ConfigDefaults,
  ];

export const defaultPersonCensorColumns: IColumn<RetssagsProeveField>[] = [
  {
    field: "proceduredato",
    title: "Dato",
    active: true,
    format: "date",
  },
  { field: "nr", title: "RP nr", active: true, format: "string" },
  {
    field: "retNavigation.navn1",
    title: "Retskreds",
    active: true,
    format: "string",
  },
  {
    field: "eksaminandNavigation.fuldtNavn",
    title: "Fuldmægtig (kandidat navn)",
    active: true,
    format: "string",
    link: (rowData: Fmuretssagsproeve) =>
      `/person/${rowData.eksaminandNavigation.id}`,
  },
  {
    field: "eksaminandNavigation.primaerPersonFirmaRelationPaa.firma.navn1",
    title: "Fuldmægtig firmanavn",
    active: true,
    format: "string",
    link: (rowData: Fmuretssagsproeve) =>
      `/firma/${rowData.eksaminandNavigation.primaerPersonFirmaRelationPaa?.firma.id}`,
  },
  ...ConfigDefaults,
];

export const defaultPersonSanktionColumns: IColumn<PersonSanktionField>[] = [
  { field: "sagsNr", title: "Sagsnr", active: true, format: "string" },
  {
    field: "naevnskendelseDato",
    title: "Kendelsesdato",
    active: true,
    format: "date",
  },
  {
    field: "naevnskendelseSanktionstypeNrNavigation.navn",
    title: "Sanktion (nævn)",
    active: true,
    format: "string",
  },
  {
    field: "naevnskendelseBoedebeloeb",
    title: "Bødebeløb (nævn)",
    active: true,
    format: "string",
  },
  {
    field: "indbragtretten",
    title: "Indbragt",
    active: true,
    format: "boolean",
  },
  {
    field: "retskendelseDato",
    title: "Domsdato",
    active: true,
    format: "date",
  },
  {
    field: "retskendelseSanktionstypeNrNavigation.navn",
    title: "Sanktion (retten)",
    active: true,
    format: "string",
  },
  {
    field: "retskendelseBoedebeloeb",
    title: "Bødebeløb (retten)",
    active: true,
    format: "string",
  },
  {
    field: "bemaerkninger",
    title: "Bemærkninger",
    active: true,
    format: "string",
  },
  {
    field: "sagsLink",
    title: "Kendelse",
    active: true,
    format: "string",
    externalLink: (rowdata) => rowdata.sagsLink,
  },
  {
    field: "afgoerelseskoder",
    title: "Afgørelseskoder",
    active: true,
    format: "string",
  },
  ...ConfigDefaults,
];

export const defaultPersonDisciplinaerStatusColumns: IColumn<PersonDisciplinaerStatusField>[] =
  [
    {
      field: "disciplinaerstatustypeNrNavigation.navn",
      title: "Status",
      active: true,
      format: "string",
    },
    {
      field: "periode.fraDato",
      title: "Startdato",
      active: true,
      format: "date",
    },
    {
      field: "periode.tilDatoInklusiv",
      title: "Slutdato",
      active: true,
      format: "date",
    },
    {
      field: "esdhSagsNr",
      title: "Sagsnr. F2",
      active: true,
      format: "string",
    },
    {
      field: "bemaerkning",
      title: "Bemærkninger",
      active: true,
      format: "string",
    },
    { field: "id", title: "Id", active: false, format: "string" },
    { field: "opretDato", title: "Oprettet", active: false, format: "date" },
    {
      field: "retteDato",
      title: "Sidst rettet",
      active: false,
      format: "date",
    },
    {
      field: "opretInit",
      title: "Oprettet af",
      active: false,
      format: "string",
    },
    {
      field: "retteInit",
      title: "Rettet af",
      active: false,
      format: "string",
    },
  ];

export const defaultPersonEfteruddannelseColumns: IColumn<PersonEfteruddannelseField>[] =
  [
    {
      field: "periode.fraDato",
      title: "Startdato",
      active: true,
      format: "date",
    },
    {
      field: "periode.tilDatoInklusiv",
      title: "Slutdato",
      active: true,
      format: "date",
    },
    /*
    {
      field: "modtagetDato",
      title: "Modtaget dato",
      active: true,
      format: "date",
    },
    */
    {
      field: "lektionerGennemfoert",
      title: "Lektioner gennemført",
      active: true,
      format: "custom",
      customValue: (value: number) => {
        return formatLessons(value);
      },
    },

    {
      field: "lektionerOverfoert",
      title: "Overførte lektioner",
      active: true,
      format: "number",
    },
    { field: "id", title: "Id", active: false, format: "string" },
    { field: "opretDato", title: "Oprettet", active: false, format: "date" },
    {
      field: "retteDato",
      title: "Sidst rettet",
      active: false,
      format: "date",
    },
    {
      field: "opretInit",
      title: "Oprettet af",
      active: false,
      format: "string",
    },
    {
      field: "retteInit",
      title: "Rettet af",
      active: false,
      format: "string",
    },
    {
      field: "registreringsDato",
      title: "Modtaget dato",
      active: true,
      format: "date",
    },
    {
      field: "indberetningFrist",
      title: "Frist",
      active: true,
      format: "date",
    },
    {
      field: "indberetningStatus",
      title: "Status",
      active: true,
      format: "string",
    },
    {
      field: "bemaerkning",
      title: "Bemærkning",
      active: true,
      format: "string",
    },
  ];

export const defaultPersonKlienterklaeringColumns: IColumn<PersonKlienterklaeringField>[] =
  [
    {
      field: "erklAar",
      title: "Erklæringsår",
      active: true,
      format: "number",
    },
    {
      field: "klienterklaeringstypeNavigation.navn",
      title: "Type",
      active: true,
      format: "string",
    },
    {
      field: "firmaCvrNr",
      title: "CVR firma",
      active: true,
      format: "number",
    },
    {
      field: "klienterklaeringStatusNavigation.navn",
      title: "Status",
      active: true,
      format: "string",
    },
    {
      field: "erklModtaget",
      title: "Modtaget dato",
      active: true,
      format: "date",
    },
    {
      field: "erklNr",
      title: "Nr. analog erklæring",
      active: true,
      format: "number",
    },
    {
      field: "id",
      title: "Id",
      active: false,
      format: "string",
    },
    {
      field: "opretDato",
      title: "Oprettet",
      active: false,
      format: "date",
    },
    {
      field: "retteDato",
      title: "Sidst rettet",
      active: false,
      format: "date",
    },
    {
      field: "opretInit",
      title: "Oprettet af",
      active: false,
      format: "string",
    },
    {
      field: "retteInit",
      title: "Rettet af",
      active: false,
      format: "string",
    },
    {
      field: "periode.fraDato",
      title: "Startdato erklæringsperiode",
      active: false,
      format: "date",
    },
    {
      field: "periode.tilDatoInklusiv",
      title: "Slutdato erklæringsperiode",
      active: false,
      format: "date",
    },
    {
      field: "frist",
      title: "Frist",
      active: true,
      format: "date",
    },
    {
      field: "koerselDato",
      title: "Kørseldato",
      active: false,
      format: "date",
    },
    {
      field: "erklNrWeb",
      title: "Nr. weberklæring",
      active: true,
      format: "number",
    },
  ];

export const defaultPersonGennemfoerteTilsynColumns: IColumn<PersonGennemfoerteTilsynField>[] =
  [
    {
      field: "gennemfoerselDato",
      title: "Gennemført",
      active: true,
      format: "date_quarter",
    },
    {
      field: "sagsNr",
      title: "Sagsnr. F2",
      active: true,
      format: "number",
    },
    {
      field: "bemaerkning",
      title: "Bemærkning",
      active: true,
      format: "string",
    },
    {
      field: "personTilsynstype",
      title: "Type",
      active: true,
      format: "custom",
      customValue: (rowData) => {
        if (typeof rowData === "string" && rowData !== null) {
          const titleCase =
            rowData.charAt(0).toLocaleUpperCase("da-DK") +
            rowData.slice(1).toLocaleLowerCase("da-DK");
          const displayName = titleCase
            .replaceAll("ae", "æ")
            .replaceAll("oe", "ø");
          return displayName;
        }
        return "";
      },
    },
    ...ConfigDefaults,
  ];

export const defaultPersonForsikringColumns: IColumn<PersonForsikringField>[] =
  [
    {
      field: "forsikringsselskab.navn1",
      title: "Forsikringsselskab",
      active: true,
      format: "string",
    },
    {
      field: "policeNr",
      title: "Policenr.",
      active: true,
      format: "string",
    },
    {
      field: "periode.fraDato",
      title: "Startdato",
      active: true,
      format: "date",
    },
    {
      field: "periode.tilDatoInklusiv",
      title: "Slutdato",
      active: true,
      format: "date",
    },
    {
      field: "garantiordning",
      title: "Garantiordning",
      active: true,
      format: "boolean",
    },
    {
      field: "restance",
      title: "Restance",
      active: true,
      format: "boolean",
    },
    {
      field: "notat",
      title: "Bemærkning",
      active: true,
      format: "string",
    },
    {
      field: "id",
      title: "Id",
      active: false,
      format: "string",
    },
    {
      field: "opretDato",
      title: "Oprettet",
      active: false,
      format: "date",
    },
    {
      field: "retteDato",
      title: "Sidst rettet",
      active: false,
      format: "date",
    },
    {
      field: "opretInit",
      title: "Oprettet af",
      active: false,
      format: "string",
    },
    {
      field: "retteInit",
      title: "Rettet af",
      active: false,
      format: "string",
    },
  ];

export const defaultPersonTilsynerklaeringColumns: IColumn<PersonTilsynerklaeringField>[] =
  [
    {
      field: "erklAar",
      title: "Erklæringsår",
      active: true,
      format: "number",
    },
    {
      field: "tilsynserklaeringStatusNavigation.navn",
      title: "Status",
      active: true,
      format: "number",
    },
    {
      field: "erklModtaget",
      title: "Modtaget dato",
      active: true,
      format: "date",
    },
    {
      field: "id",
      title: "Id",
      active: false,
      format: "string",
    },
    {
      field: "opretDato",
      title: "Oprettet",
      active: false,
      format: "date",
    },
    {
      field: "retteDato",
      title: "Sidst rettet",
      active: false,
      format: "date",
    },
    {
      field: "opretInit",
      title: "Oprettet af",
      active: false,
      format: "string",
    },
    {
      field: "retteInit",
      title: "Rettet af",
      active: false,
      format: "string",
    },
    {
      field: "frist",
      title: "Frist",
      active: true,
      format: "date",
    },
    {
      field: "koerselDato",
      title: "Kørseldato",
      active: false,
      format: "date",
    },
    {
      field: "forsikringDaekning",
      title: "Forsikringsdækning",
      active: false,
      format: "string",
    },
    {
      field: "forsikringPolice",
      title: "Policenr.",
      active: false,
      format: "string",
    },
    {
      field: "forsikringSelskab",
      title: "Forsikringsselskab",
      active: false,
      format: "string",
    },
  ];

export const defaultPersonBidragColumns: IColumn<
  PersonBidragsfakturaField<1>
>[] = [
  {
    field: "dato",
    active: true,
    format: "date",
    title: "Dato",
  },
  {
    field: "opkraevningsaar",
    active: true,
    format: "string",
    title: "Opkrævningsår",
  },
  {
    field: "fakturaNr",
    active: true,
    format: "number",
    title: "FakturaNr",
  },
  {
    field: "faktureretBeloeb",
    active: true,
    format: "string",
    title: "Faktureret beløb",
  },
  {
    field: "fakturaStatusNavigation.navn",
    active: true,
    format: "string",
    title: "Fakturastatus",
  },
  {
    field: "bidragsmodtagerNavigation.navn",
    active: true,
    format: "string",
    title: "Bidragsmodtager",
  },
  ...ConfigDefaults,
];
